/**
 * 🔧 RENTAL SETTINGS SERVICE
 * Manages admin rental configuration including address release timing
 * Backend: /api/admin/settings/rental-settings
 */

import { getApiBaseUrl } from '@/config/environment';


export interface AddressReleaseSettings {
  hoursBeforePickup: number; // Global default
  serviceSpecific?: {
    selfDrive?: number;
    driverHire?: number;
    withDriver?: number;
  };
}

export interface RentalSettings {
  addressRelease: AddressReleaseSettings;
  updatedAt?: string;
  updatedBy?: string;
}

export interface AddressReleaseLog {
  id: string;
  bookingNumber: string;
  bookingId: string;
  address: string;
  releasedAt: string;
  serviceType: string;
  renterName: string;
}

export interface RentalSettingsResponse {
  success: boolean;
  data?: RentalSettings;
  error?: string;
}

export interface AddressReleaseHistoryResponse {
  success: boolean;
  data?: {
    logs: AddressReleaseLog[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  error?: string;
}

class RentalSettingsService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${getApiBaseUrl()}${endpoint}`;
    const token = localStorage.getItem('token');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get current rental settings
   */
  async getSettings(): Promise<RentalSettingsResponse> {
    try {
      const response = await this.request<RentalSettingsResponse>(
        '/api/admin/settings/rental-settings'
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get rental settings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update rental settings
   */
  async updateSettings(settings: Partial<RentalSettings>): Promise<RentalSettingsResponse> {
    try {
      const response = await this.request<RentalSettingsResponse>(
        '/api/admin/settings/rental-settings',
        {
          method: 'PATCH',
          body: JSON.stringify(settings),
        }
      );
      return response;
    } catch (error: any) {
      console.error('Failed to update rental settings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get address release history
   */
  async getAddressReleaseHistory(page = 1, limit = 20): Promise<AddressReleaseHistoryResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await this.request<AddressReleaseHistoryResponse>(
        `/api/admin/settings/address-release-history?${params.toString()}`
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get address release history:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Calculate when address will be released based on pickup time
   */
  calculateReleaseTime(pickupTime: Date, hoursBeforePickup: number): Date {
    const releaseTime = new Date(pickupTime);
    releaseTime.setHours(releaseTime.getHours() - hoursBeforePickup);
    return releaseTime;
  }

  /**
   * Format time remaining until address release
   */
  formatTimeUntilRelease(pickupTime: Date, hoursBeforePickup: number): string {
    const releaseTime = this.calculateReleaseTime(pickupTime, hoursBeforePickup);
    const now = new Date();
    const diffMs = releaseTime.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'Released';
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }

    return `${hours}h ${minutes}m`;
  }
}

export default new RentalSettingsService();
